import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import { Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Shared marketing landing shell for the two apps (AMSaaS / AMCaaS). Mirrors the
 * AuthShell pattern: one shell, two thin host variants pass content + accent.
 *
 * App-branded only (WL-03) — the landing shows the APP identity and its own accent,
 * never a provider theme (that applies post-login). The "two doors" sign-in section
 * links to the neutral login routes; the host decides which variant of each door.
 */

export interface LandingDoor {
  href: "/login/provider" | "/login/client" | "/login/platform";
  eyebrow: string; // "Provider" / "Client" / "Platform"
  title: string; // "Provider sign-in"
  blurb: string;
  bullets: string[];
  icon: LucideIcon;
  /** Brand-filled panel (the primary door) vs light bordered panel. */
  primary?: boolean;
}

export interface LandingShellProps {
  appName: string;
  appTagline: string;
  logoSrc: string;
  logoSrc2x?: string;
  /** Accent hex + a second hue for gradients. */
  accent: string;
  accent2: string;
  badge: string;
  /** Headline with an emphasised fragment (use <Accent> for the gradient bit). */
  headline: ReactNode;
  blurb: string;
  stats: { value: string; label: string }[];
  features: { icon: LucideIcon; title: string; blurb: string }[];
  doors: [LandingDoor, LandingDoor];
  /** Extra line under the doors (e.g. "New to AMCaaS? Create an account"). */
  footerCta?: ReactNode;
}

export function LandingShell(props: LandingShellProps): JSX.Element {
  const {
    appName,
    appTagline,
    logoSrc,
    logoSrc2x,
    accent,
    accent2,
    badge,
    headline,
    blurb,
    stats,
    features,
    doors,
    footerCta,
  } = props;

  const grad = `linear-gradient(90deg, ${accent}, ${accent2})`;
  const srcSet = logoSrc2x ? `${logoSrc} 1x, ${logoSrc2x} 2x` : undefined;

  return (
    <div className="min-h-full bg-background text-foreground">
      {/* soft brand glow */}
      <div className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 right-0 size-[38rem] rounded-full opacity-10 blur-3xl"
          style={{ backgroundImage: grad }}
        />
        <div className="relative mx-auto max-w-6xl px-6">
          {/* nav */}
          <nav className="flex items-center justify-between gap-4 py-5">
            <div className="flex min-w-0 items-center gap-3">
              <img
                src={logoSrc}
                srcSet={srcSet}
                alt={appName}
                className="h-8 w-auto shrink-0 object-contain dark:brightness-150"
              />
              <span className="hidden truncate text-sm font-medium text-muted-foreground sm:inline">
                {appTagline}
              </span>
            </div>
            <a
              href="#signin"
              className="shrink-0 rounded-full px-4 py-2 text-sm font-medium text-white"
              style={{ background: accent }}
            >
              Log in
            </a>
          </nav>

          {/* split hero */}
          <div className="grid items-center gap-10 py-14 lg:grid-cols-2 lg:py-20">
            <div>
              {/* Prominent app identity — the app title (acronym) + full expansion. */}
              <div className="mb-6 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span
                  className="text-3xl font-black tracking-tight sm:text-4xl"
                  style={{ color: accent }}
                >
                  {appName}
                </span>
                <span className="text-lg font-semibold text-muted-foreground sm:text-xl">
                  {appTagline}
                </span>
              </div>
              <Badge variant="outline" className="font-normal">
                {badge}
              </Badge>
              <h1 className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
                {headline}
              </h1>
              <p className="mt-5 max-w-md text-lg text-muted-foreground">
                {blurb}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#signin"
                  className="rounded-full px-6 py-3 font-semibold text-white shadow-lg"
                  style={{ background: accent }}
                >
                  Sign in
                </a>
                <a
                  href="#features"
                  className={cn(
                    buttonVariants({ variant: "outline", size: "lg" }),
                    "rounded-full",
                  )}
                >
                  Explore features
                </a>
              </div>
            </div>

            {/* product mock */}
            <div className="rounded-2xl border bg-muted/40 p-3 shadow-xl">
              <div className="rounded-xl bg-background p-5 shadow-sm">
                <div className="mb-4 flex gap-1.5">
                  <span className="size-2.5 rounded-full bg-muted-foreground/20" />
                  <span className="size-2.5 rounded-full bg-muted-foreground/20" />
                  <span className="size-2.5 rounded-full bg-muted-foreground/20" />
                </div>
                <div className="space-y-3">
                  <div className="h-3 w-2/3 rounded bg-muted-foreground/15" />
                  <div className="grid grid-cols-3 gap-3">
                    <div
                      className="h-16 rounded-lg opacity-90"
                      style={{ backgroundImage: grad }}
                    />
                    <div className="h-16 rounded-lg bg-muted" />
                    <div className="h-16 rounded-lg bg-muted" />
                  </div>
                  <div className="h-24 rounded-lg bg-muted" />
                </div>
              </div>
            </div>
          </div>

          {/* stats */}
          <div className="grid gap-6 border-y py-8 text-center sm:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label}>
                <p
                  className="text-3xl font-extrabold"
                  style={{ color: accent }}
                >
                  {s.value}
                </p>
                <p className="text-sm text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>

          {/* features */}
          <div id="features" className="grid gap-4 py-14 sm:grid-cols-3">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <Card key={f.title} className="shadow-sm">
                  <CardContent className="p-6">
                    <div
                      className="mb-3 grid size-10 place-items-center rounded-xl"
                      style={{ background: `${accent}1a`, color: accent }}
                    >
                      <Icon className="size-5" />
                    </div>
                    <h3 className="font-semibold">{f.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {f.blurb}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* sign-in — two big doors (Option C) */}
      <div id="signin" className="mx-auto max-w-6xl px-6">
        <div className="my-16">
          <h2 className="text-center text-3xl font-bold tracking-tight">
            Sign in to your account
          </h2>
          <p className="mb-9 mt-2 text-center text-muted-foreground">
            Pick your door.
          </p>
          <div className="grid gap-5 md:grid-cols-2">
            {doors.map((door) => (
              <DoorPanel
                key={door.href}
                door={door}
                accent={accent}
                grad={grad}
              />
            ))}
          </div>
          {footerCta && (
            <p className="mt-8 text-center text-sm text-muted-foreground">
              {footerCta}
            </p>
          )}
        </div>
      </div>

      <footer className="border-t py-10 text-center text-sm text-muted-foreground">
        {appName} — {appTagline}
      </footer>
    </div>
  );
}

/** Accent-gradient text fragment for the headline. */
export function Accent({
  children,
  from,
  to,
}: {
  children: ReactNode;
  from: string;
  to: string;
}): JSX.Element {
  return (
    <span
      style={{
        backgroundImage: `linear-gradient(90deg, ${from}, ${to})`,
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
      }}
    >
      {children}
    </span>
  );
}

function DoorPanel({
  door,
  accent,
  grad,
}: {
  door: LandingDoor;
  accent: string;
  grad: string;
}): JSX.Element {
  const Icon = door.icon;

  if (door.primary) {
    // Brand-filled door.
    return (
      <Link
        to={door.href}
        className="group relative overflow-hidden rounded-3xl p-8 text-white shadow-xl transition hover:-translate-y-1"
        style={{ backgroundImage: grad }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-10 -top-10 size-40 rounded-full bg-white/15 blur-2xl"
        />
        <div className="relative">
          <span className="grid size-12 place-items-center rounded-2xl bg-white/20">
            <Icon className="size-6" />
          </span>
          <h3 className="mt-5 text-2xl font-bold">{door.title}</h3>
          <p className="mt-2 max-w-xs text-white/80">{door.blurb}</p>
          <ul className="mt-5 space-y-2 text-sm text-white/90">
            {door.bullets.map((b) => (
              <li key={b} className="flex items-center gap-2">
                <Check className="size-4 shrink-0" /> {b}
              </li>
            ))}
          </ul>
          <span className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 font-semibold">
            <span style={{ color: accent }}>Continue →</span>
          </span>
        </div>
      </Link>
    );
  }

  // Light bordered door.
  return (
    <Link
      to={door.href}
      className="group relative overflow-hidden rounded-3xl border-2 p-8 shadow-sm transition hover:-translate-y-1"
    >
      <div className="relative">
        <span
          className="grid size-12 place-items-center rounded-2xl"
          style={{ background: `${accent}1a`, color: accent }}
        >
          <Icon className="size-6" />
        </span>
        <h3 className="mt-5 text-2xl font-bold">{door.title}</h3>
        <p className="mt-2 max-w-xs text-muted-foreground">{door.blurb}</p>
        <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
          {door.bullets.map((b) => (
            <li key={b} className="flex items-center gap-2">
              <Check className="size-4 shrink-0" style={{ color: accent }} />{" "}
              {b}
            </li>
          ))}
        </ul>
        <span
          className="mt-7 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold text-white"
          style={{ background: accent }}
        >
          Continue →
        </span>
      </div>
    </Link>
  );
}
